/**
 * Clasificación de impuestos en el cliente.
 *
 * El total de IVA se rederiva en el front (sobre todo en el formulario de
 * factura, que recalcula en vivo a medida que el usuario edita y no usa el
 * iva_total ya calculado por el backend). Antes esto exigía igualdad exacta
 * con el literal "IVA", así que un nombre como "IVA 19%" no se reconocía y la
 * línea se caía del total de IVA hacia "otros impuestos", dejando mal la base
 * imponible de ReteIva.
 *
 * Esto espeja conceptualmente la clasificación del backend: el nombre
 * normalizado (mayúsculas, sin puntos ni espacios) que EMPIEZA con "IVA"
 * cuenta como IVA, además de la convención chilena `tax_type === "IVA"`.
 */

import type Withholding from "@/types/withholding";

export interface TaxLike {
  id?: string | number;
  tax_name?: string | null;
  tax_type?: string | null;
  tax_type_code?: string | null;
  percentage?: number | null;
  taxable_amount?: number | null;
  tax_amount?: number | null;
  line_tax_ids?: number[];
}

function normalizeTaxName(name?: string | null): string {
  return (name ?? "").toUpperCase().replace(/[\s.]/g, "");
}

export function isIvaTax(tax: TaxLike, country?: string | null): boolean {
  const code = tax.tax_type_code || tax.tax_type;
  if (country === "CO" && ["01", "04", "05", "06", "07"].includes(code || "")) return code === "01";
  if (country === "CO" && isWithholdingTax(tax, country)) return false;
  if (country === "MX" && code === "002") return true;
  if (tax?.tax_type === "IVA") return true;
  return normalizeTaxName(tax?.tax_name).startsWith("IVA");
}

const coWithholdings: Record<string, string> = { "05": "ReteIva", "06": "ReteFuente", "07": "ReteIca" };

export function withholdingFamily(tax: TaxLike, country?: string | null): string {
  const code = tax.tax_type_code || tax.tax_type || "";
  if (country === "CO" && coWithholdings[code]) return coWithholdings[code];
  for (const name of Object.values(coWithholdings)) {
    if ([code, tax.tax_type, tax.tax_name].some(value => value?.toUpperCase() === name.toUpperCase())) return name;
  }
  return code || tax.tax_name || "";
}

export function isWithholdingTax(tax: TaxLike, country?: string | null): boolean {
  if (country !== "CO") return false;
  const code = tax.tax_type_code || tax.tax_type || "";
  if (["01", "04"].includes(code)) return false;
  return Object.values(coWithholdings).includes(withholdingFamily(tax, country));
}

export function projectTaxes<T extends TaxLike>(
  taxes: T[], withholdings: TaxLike[], country?: string | null,
): { taxes: T[]; withholdings: Withholding[] } {
  const result: Withholding[] = (withholdings || []).map(w => ({
    ...w, id: String(w.id ?? ""), tax_type: w.tax_type || w.tax_name || w.tax_type_code || "",
    tax_type_code: w.tax_type_code ?? undefined, percentage: w.percentage ?? undefined,
    taxable_amount: w.taxable_amount ?? undefined, tax_amount: w.tax_amount ?? undefined,
    line_tax_ids: w.line_tax_ids ? [...w.line_tax_ids] : undefined,
  }));
  // A newly projected relation is not evidence that another relation is its mirror.
  const jsonRows = [...result];
  const additive: T[] = [];
  for (const tax of taxes || []) {
    if (!isWithholdingTax(tax, country)) {
      additive.push(tax);
      continue;
    }
    const family = withholdingFamily(tax, country);
    const matches = jsonRows.filter(w => withholdingFamily(w, country) === family);
    let wh = matches.find(w => typeof tax.id === "number" && w.line_tax_ids?.includes(tax.id));
    if (!wh) {
      const exact = matches.filter(w => !w.line_tax_ids?.length
        && (w.percentage != null || w.tax_amount != null)
        && w.percentage == tax.percentage && w.taxable_amount == tax.taxable_amount && w.tax_amount == tax.tax_amount);
      if (exact.length === 1) wh = exact[0];
    }
    if (!wh) {
      const placeholders = matches.filter(w => !w.line_tax_ids?.length && w.percentage == null && w.tax_amount == null);
      if (placeholders.length === 1) wh = placeholders[0];
    }
    if (!wh) {
      wh = { id: "", tax_type: family, percentage: undefined, taxable_amount: undefined, tax_amount: undefined };
      result.push(wh);
    }
    if (wh.percentage == null && wh.tax_amount == null) {
      Object.assign(wh, { tax_type_code: tax.tax_type_code || tax.tax_type, percentage: tax.percentage ?? undefined,
        taxable_amount: tax.taxable_amount ?? undefined, tax_amount: tax.tax_amount ?? undefined });
    }
    if (typeof tax.id === "number") wh.line_tax_ids = [...new Set([...(wh.line_tax_ids || []), tax.id])];
  }
  return { taxes: additive, withholdings: result };
}
